import {
  setDebug,
  themeParams,
  initData,
  viewport,
  init as initSDK,
  miniApp,
  backButton,
  swipeBehavior,
} from '@tma.js/sdk-react';

import { applicaAltezzaRadice } from '@/lib/altezzaRadice';

interface InitOptions {
  debug: boolean;
  /** Load Eruda mobile dev console (iOS/Android only, dev builds only) */
  eruda?: boolean;
  /** reserved — Telegram-for-macOS theme workaround can be added later if needed */
  mockForMacOS?: boolean;
  /** Il colore del chrome nativo prima che il motore dei temi dipinga (default: nero). La v3 prenotata passa il suo. */
  coloreChrome?: string;
}

interface TgWebApp {
  setBackgroundColor?: (color: string) => void;
  setHeaderColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  isVersionAtLeast?: (version: string) => boolean;
}

/**
 * Paints the *native* Telegram chrome (header bar, the background behind the
 * WebView, and the bottom bar) with an explicit RGB colour so there's no
 * off-black / white framing around the Mini App. Without this, telegram-web-app.js
 * defaults the bottom bar to white and binds the header to the user's Telegram
 * theme `bg_color` (white on light themes).
 *
 * Driven through the canonical `window.Telegram.WebApp` global (guaranteed
 * loaded via the script tag in index.html) rather than the @tma.js wrappers,
 * whose `isAvailable()` checks are conservative across client versions. Hex
 * header/bottom-bar colours need Bot API 7.10+; older clients only accept a
 * theme key for the header (`bg_color` matches the surface we just set) and
 * have no bottom bar. Best-effort + try/catch: no-ops outside Telegram. The
 * theme engine (`lib/appearance.ts`) re-calls this with the active theme bg.
 */
export function setTelegramChrome(color: string): void {
  // Preferred: @tma.js wrappers — correct per-client version detection, guarded
  // by isAvailable()/supports('rgb') so they no-op cleanly when unsupported.
  try {
    if (miniApp.setBgColor.isAvailable()) miniApp.setBgColor(color);
  } catch {
    /* noop */
  }
  try {
    if (miniApp.setBottomBarColor.isAvailable()) miniApp.setBottomBarColor(color);
  } catch {
    /* noop */
  }
  try {
    if (miniApp.setHeaderColor.isAvailable() && miniApp.setHeaderColor.supports('rgb')) {
      miniApp.setHeaderColor(color);
    }
  } catch {
    /* noop */
  }

  // Fallback: the telegram-web-app.js global owns the white-bottom-bar /
  // bg_color-header defaults; override it directly for clients where the
  // wrappers above stay unavailable. Hex needs Bot API 7.10+; older clients
  // only accept a theme key for the header (bg_color matches the surface).
  try {
    const wa = (window as unknown as { Telegram?: { WebApp?: TgWebApp } }).Telegram?.WebApp;
    if (wa) {
      const ok710 = typeof wa.isVersionAtLeast === 'function' && wa.isVersionAtLeast('7.10');
      wa.setBackgroundColor?.(color);
      if (ok710) {
        wa.setHeaderColor?.(color);
        wa.setBottomBarColor?.(color);
      } else {
        wa.setHeaderColor?.('bg_color');
      }
    }
  } catch {
    /* best-effort — no-op outside Telegram / on unsupported clients */
  }
}

/** Quello che serve del `Telegram.WebApp` per bloccare l'orientamento (Bot API 8.0: `lockOrientation`, `isOrientationLocked`). */
interface TgOrientamento {
  lockOrientation?: () => void;
  isOrientationLocked?: boolean;
  isVersionAtLeast?: (version: string) => boolean;
}

/** Quello che serve della finestra: la misura (verticale = piu alta che larga) e gli eventi con cui si accorge che il telefono e stato girato. */
interface FinestraOrientamento {
  innerWidth: number;
  innerHeight: number;
  addEventListener: (tipo: string, f: () => void) => void;
  removeEventListener: (tipo: string, f: () => void) => void;
}

export type EsitoBloccoOrientamento = 'bloccato' | 'in-attesa-del-verticale' | 'gia-bloccato' | 'non-disponibile';

/**
 * Blocca la rotazione della mini app IN VERTICALE (I-807, Luca: «dobbiamo prevenirla... non sapendo se gli utenti hanno il blocco dell'autorotazione»): su TUTTA la TMA, all'avvio.
 * Il palco orizzontale dei tavoli si apre solo col TASTO, mai girando il telefono (in una WebView che ruota il tavolo in piedi si deformava in un viewport orizzontale).
 * ⚠️ `lockOrientation()` di Telegram fissa l'orientamento CORRENTE, non una scelta: se la mini app si apre col telefono di traverso NON si blocca li (fisserebbe l'orizzontale), si aspetta
 * il primo momento in cui e in verticale (`resize` / `orientationchange`) e si blocca allora. Il `@tma.js/sdk` installato non espone il blocco (`device_orientation_*` sono i sensori, non
 * il blocco): si usa il `Telegram.WebApp` canonico, come `setTelegramChrome`. Dove non c'e (Telegram precedente alla 8.0, il web) non succede niente e non si lancia niente:
 * la TMA resta com'era, libera di ruotare.
 */
export function bloccaOrientamentoVerticale(
  wa: TgOrientamento | undefined = (window as unknown as { Telegram?: { WebApp?: TgOrientamento } }).Telegram?.WebApp,
  finestra: FinestraOrientamento = window,
): EsitoBloccoOrientamento {
  try {
    if (!wa || typeof wa.lockOrientation !== 'function' || typeof wa.isVersionAtLeast !== 'function' || !wa.isVersionAtLeast('8.0')) return 'non-disponibile';
    if (wa.isOrientationLocked === true) return 'gia-bloccato';
    const inVerticale = () => finestra.innerHeight >= finestra.innerWidth;
    if (inVerticale()) {
      wa.lockOrientation();
      return 'bloccato';
    }
    const aspetta = () => {
      if (!inVerticale()) return;
      finestra.removeEventListener('resize', aspetta);
      finestra.removeEventListener('orientationchange', aspetta);
      try {
        wa.lockOrientation?.();
      } catch {
        /* best-effort */
      }
    };
    finestra.addEventListener('resize', aspetta);
    finestra.addEventListener('orientationchange', aspetta);
    return 'in-attesa-del-verticale';
  } catch {
    return 'non-disponibile';
  }
}

/**
 * Initializes the Telegram SDK and mounts all components used by the app.
 * Mirrors the official @tma.js/sdk-react template init flow.
 */
export async function initTelegram(options: InitOptions): Promise<void> {
  setDebug(options.debug);
  initSDK();

  if (options.eruda) {
    try {
      const { default: eruda } = await import('eruda');
      eruda.init();
      console.info('[telegram] Eruda dev console initialized');
    } catch (err) {
      console.warn('[telegram] failed to init Eruda:', err);
    }
  }

  backButton.mount.ifAvailable();
  initData.restore();

  if (miniApp.mount.isAvailable()) {
    themeParams.mount();
    miniApp.mount();
    themeParams.bindCssVars();
    // Baseline: black native chrome. The theme engine's one-shot apply in
    // main.tsx immediately re-paints this with the user's active theme bg.
    // Con la v3 prenotata (`coloreChromeIniziale`, lib/v3.ts) il baseline e' il suo sfondo, non il nero.
    setTelegramChrome(options.coloreChrome ?? '#000000');
  }

  // Expand the WebView to full height + lock the vertical swipe-to-close
  // gesture. Without disableVertical(), pulling down inside scrollable
  // content can dismiss the Mini App by mistake (Bot API 7.7+; older
  // clients silently no-op).
  if (viewport.mount.isAvailable()) {
    void viewport.mount().then(() => {
      // ESPANDI PRIMA, lega dopo.
      //
      // `--tg-viewport-stable-height` è l'altezza di #root (globals.css): se il
      // legame fotografa lo stato NON espanso, lo shell resta alto quanto la
      // mini app collassata — circa il 60% dello schermo — e sotto compare la
      // texture nuda con la barra di navigazione ferma a metà pagina.
      //
      // Chiedendo l'espansione prima, il primo valore legato è già quello
      // giusto invece di dipendere dal fatto che l'aggiornamento arrivi.
      if (viewport.expand.isAvailable()) viewport.expand();
      viewport.bindCssVars();

      // La guardia sull'altezza della radice (r16, I-438): un `stableHeight` guasto — 0 al mount «non stabile», e su un
      // reload ripristinato tale e quale da sessionStorage — schiaccia `#root` a una striscia con la sola TabBar. V.
      // `lib/altezzaRadice.ts`. Non tocca la variabile ne' lo storage dell'SDK: agisce solo sull'altezza inline della radice.
      const guardiaAltezza = () =>
        applicaAltezzaRadice(document.getElementById('root'), viewport.stableHeight(), window.innerHeight);
      guardiaAltezza();
      viewport.stableHeight.sub(guardiaAltezza);
      window.addEventListener('resize', guardiaAltezza);

      // Ri-asserzione dopo un giro di eventi: su alcuni client l'espansione
      // chiesta troppo presto viene ignorata in silenzio, e senza questa
      // seconda chiamata l'app resta collassata per tutta la sessione.
      setTimeout(() => {
        if (viewport.expand.isAvailable()) viewport.expand();
      }, 300);
    });
  }

  if (swipeBehavior.mount.isAvailable()) {
    swipeBehavior.mount();
    if (swipeBehavior.disableVertical.isAvailable()) {
      swipeBehavior.disableVertical();
    }
  }

  // La rotazione resta bloccata in verticale su tutta la TMA (I-807): il palco orizzontale si apre solo col tasto del tavolo.
  bloccaOrientamentoVerticale();
}
